import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-2';
import Underline from '@tiptap/extension-underline';
import CharacterCount from '@tiptap/extension-character-count';
import boldSvg from 'assets/svg/text-formatting-section/text-formatting-bold.svg';
import italicSvg from 'assets/svg/text-formatting-section/text-formatting-italic.svg';
import underlineSvg from 'assets/svg/text-formatting-section/text-formatting-underline.svg';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import CommonAudio from 'mixins/CommonAudio';
import { Selection } from 'prosemirror-state';
import Focus from '@tiptap/extension-focus';
import FakeTextCursor from './extensions/FakeTextCursor';
import DisableSpecialCommands from './extensions/DisableSpecialCommands';
import HintableText from './extensions/HintableText';
import KeyboardAdaptationWrapper from '../../activity/KeyboardAdaptationWrapper.vue';
import mixins from 'vue-typed-mixins';
import { activity, keyboard, mapState, mapMutations } from '../../../store/helpers';
import { mapGetters } from 'vuex';
export const TextEditor = mixins(TypingActivityHelper, CommonAudio).extend({
    components: {
        EditorContent,
        KeyboardAdaptationWrapper
    },
    data() {
        return {
            editor: null,
            svg: {
                boldSvg,
                italicSvg,
                underlineSvg
            },
            __maxLengthExceeded: false
        };
    },
    computed: Object.assign(Object.assign(Object.assign({}, mapGetters('application', ['usingExternalKeyboard'])), mapState(activity, ['viewedIntro'])), { showMaxLength() {
            return this.maxLength !== Infinity;
        } }),
    props: {
        maxLength: {
            type: Number,
            default: 3000,
            required: false
        },
        autoFocus: {
            type: Boolean,
            default: false,
            required: false
        },
        editable: {
            type: Boolean,
            default: true,
            required: false
        },
        placeholder: String,
        requirePlaceholder: Boolean,
        value: {
            type: String,
            default: ''
        },
        rootStyles: {
            type: [Object, undefined],
            default: undefined
        },
        fakeCursor: {
            type: Boolean,
            default: true
        },
        keyboardAdaptationWrapperMinElementHeight: {
            type: Number,
            default: 100,
            required: false
        }
    },
    methods: Object.assign(Object.assign({}, mapMutations(keyboard, ['disableTopLevelInput', 'enableTopLevelInput'])), { handleBackspace(commands, transaction) {
            commands.first(({ commands }) => [
                // First, let Tiptap handle according to it's implementation here:
                // https://github.com/ueberdosis/tiptap/blob/8c6751f0c638effb22110b62b40a1632ea6867c9/packages/core/src/extensions/keymap.ts#L13
                () => commands.undoInputRule(),
                // maybe convert first text block node to default node
                () => commands.command(({ tr }) => {
                    const { selection, doc } = tr;
                    const { empty, $anchor } = selection;
                    const { pos, parent } = $anchor;
                    const isAtStart = Selection.atStart(doc).from === pos;
                    if (!empty || !isAtStart || !parent.type.isTextblock || parent.textContent.length) {
                        return false;
                    }
                    return commands.clearNodes();
                }),
                () => commands.deleteSelection(),
                () => commands.joinBackward(),
                () => commands.selectNodeBackward(),
                // Fallback by handling the native backspace manually
                () => commands.deleteRange({
                    from: transaction.selection.$anchor.pos - 1,
                    to: transaction.selection.$anchor.pos
                })
            ]);
        },
        handleEnter(commands) {
            // Handle according to implementation
            // https://github.com/ueberdosis/tiptap/blob/8c6751f0c638effb22110b62b40a1632ea6867c9/packages/core/src/extensions/keymap.ts#L13
            commands.first(({ commands }) => [
                () => commands.newlineInCode(),
                () => commands.createParagraphNear(),
                () => commands.liftEmptyBlock(),
                () => commands.splitBlock()
            ]);
        },
        shouldHandleKey(key) {
            // Don't handle Ctrl and Alt keys.
            return !/^ctrl|alt$/.test(key);
        },
        addEditorContainerFocusStyles() {
            var _a;
            const editorContainerEl = (_a = document.getElementsByClassName('editor-container')) === null || _a === void 0 ? void 0 : _a[0];
            if (editorContainerEl)
                editorContainerEl.style.outline = '5px auto #015fcc';
        },
        removeEditorContainerFocusStyles() {
            var _a;
            const editorContainerEl = (_a = document.getElementsByClassName('editor-container')) === null || _a === void 0 ? void 0 : _a[0];
            if (editorContainerEl)
                editorContainerEl.style.outline = 'none';
        },
        focusOnEditor() {
            var _a;
            const editorContainerEl = (_a = document.getElementsByClassName('ProseMirror')) === null || _a === void 0 ? void 0 : _a[0];
            if (editorContainerEl)
                editorContainerEl.focus();
        },
        handleCorrectKey({ character, withCtrl }) {
            var _a;
            this.focusOnEditor();
            const char = character.toLowerCase();
            const self = this;
            (_a = this.editor) === null || _a === void 0 ? void 0 : _a.chain().command(({ tr, commands, editor }) => {
                const charCount = editor.storage.characterCount.characters();
                if (char === 'backspace') {
                    if (charCount > 0) {
                        if (this.placeholder) {
                            const hint = this.placeholder.substr(charCount - 1);
                            commands.updateAttributes('paragraph', { hint });
                        }
                        this.handleBackspace(commands, tr);
                    }
                }
                else if (this.requirePlaceholder &&
                    this.placeholder &&
                    character !== this.placeholder.charAt(charCount)) {
                    this.sndError();
                }
                else if (char === 'tab') {
                    tr.insertText('');
                }
                else if (char === 'enter') {
                    this.handleEnter(commands);
                }
                else if (withCtrl) {
                    if (char === 'b') {
                        this.toggleBold();
                    }
                    if (char === 'i') {
                        this.toggleItalic();
                    }
                    if (char === 'u') {
                        this.toggleUnderline();
                    }
                    tr.insertText('');
                }
                else if (self.shouldHandleKey(character)) {
                    if (this.placeholder) {
                        const hint = this.placeholder.substr(charCount + 1);
                        commands.updateAttributes('paragraph', { hint });
                    }
                    tr.insertText(character);
                }
                return true;
            }).run();
        },
        toggleBold() {
            this.$nextTick(() => {
                this.editor &&
                    this.editor
                        .chain()
                        .toggleBold()
                        .run();
            });
        },
        toggleItalic() {
            this.$nextTick(() => {
                this.editor &&
                    this.editor
                        .chain()
                        .toggleItalic()
                        .run();
            });
        },
        toggleUnderline() {
            this.$nextTick(() => {
                this.editor &&
                    this.editor
                        .chain()
                        .toggleUnderline()
                        .run();
            });
        },
        buildEditor({ autoFocus, editable, fakeCursor, maxLength, placeholder, value = '' }) {
            const self = this;
            const extensions = [
                StarterKit,
                Underline,
                DisableSpecialCommands,
                CharacterCount.configure({
                    limit: maxLength + 1
                }),
                Focus
            ];
            // @ts-ignore
            if (fakeCursor || this.usingExternalKeyboard) {
                extensions.push(FakeTextCursor);
            }
            if (placeholder) {
                extensions.push(HintableText);
            }
            const editor = new Editor({
                content: value,
                autofocus: autoFocus,
                editable,
                extensions,
                onTransaction({ editor, transaction }) {
                    if (editor.storage.characterCount.characters() <= maxLength)
                        return;
                    self.sndError();
                    // Mutex lock async changes to avoid concurrent updates
                    if (!self.__maxLengthExceeded) {
                        self.__maxLengthExceeded = true;
                        setTimeout(() => {
                            self.handleBackspace(editor.commands, transaction);
                            self.$nextTick(() => {
                                self.__maxLengthExceeded = false;
                            });
                        }, 200);
                    }
                    return false;
                },
                onFocus() {
                    self.addEditorContainerFocusStyles();
                },
                onBlur() {
                    // editor.commands.focus();
                    self.removeEditorContainerFocusStyles();
                },
                onUpdate: ({ editor }) => {
                    const isEmpty = editor.state.doc.textContent.length === 0;
                    const value = isEmpty ? '' : editor.getHTML();
                    this.$emit('input', value);
                },
                onCreate({ editor }) {
                    if (autoFocus) {
                        setTimeout(() => {
                            editor.commands.focus();
                            // Give the editor enough time to bootstrap itself after creation
                        }, 2000);
                    }
                },
                editorProps: {
                    handleDOMEvents: {
                        keydown: (view, event) => {
                            this.$root.$refs.__ExternalKeyboard__ &&
                                // @ts-ignore TODO(kevin): add types for ExternalKeyboard component
                                this.$root.$refs.__ExternalKeyboard__.handleKeyDown(event);
                        },
                        keyup: (view, event) => {
                            this.$root.$refs.__ExternalKeyboard__ &&
                                // @ts-ignore TODO(kevin): add types for ExternalKeyboard component
                                this.$root.$refs.__ExternalKeyboard__.handleKeyUp(event);
                        }
                    }
                }
            });
            editor.view.dom.setAttribute('inputmode', 'none');
            editor.view.dom.setAttribute('tabIndex', '10');
            editor.view.dom.style.caretColor =
                // @ts-ignore
                this.isMobile && !this.usingExternalKeyboard ? 'black' : 'transparent';
            if (placeholder) {
                editor.commands.updateAttributes('paragraph', { hint: placeholder });
            }
            editor.view.dom.setAttribute('spellcheck', 'false');
            return editor;
        } }),
    created() {
        this.enableTopLevelInput();
    },
    mounted() {
        this.$watch(
        //@ts-ignore
        vm => [vm.maxLength, vm.autoFocus, vm.fakeCursor, vm.placeholder, this.viewedIntro], () => {
            if (this.editor)
                this.editor.destroy();
            this.editor = this.buildEditor(this);
        }, {
            immediate: true
        });
    },
    beforeDestroy() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.destroy();
        this.disableTopLevelInput();
    },
    watch: {
        value(value) {
            var _a, _b;
            const isSame = ((_a = this.editor) === null || _a === void 0 ? void 0 : _a.getHTML()) === value;
            if (isSame)
                return;
            (_b = this.editor) === null || _b === void 0 ? void 0 : _b.commands.setContent(value, false);
        }
    }
});
export default TextEditor;
